// component-faq-tabs.js
// <faq-tabs> — turns the FAQ category blocks into filter tabs: builds one tab per
// .faq-category (from its title), plus an optional leading "All" tab, and shows
// the matching category (or all of them) at a time.
//
//   <faq-tabs data-show-all="true" data-all-label="All"> … </faq-tabs>
//
// Progressive enhancement: without JS every category is visible (the tablist is
// empty and the panels render normally).
if (!customElements.get("faq-tabs")) {
  customElements.define(
    "faq-tabs",
    class FaqTabs extends HTMLElement {
      constructor() {
        super();
        this._onClick = this._onClick.bind(this);
        this._onKeydown = this._onKeydown.bind(this);
      }

      connectedCallback() {
        this.tablist = this.querySelector("[data-faq-tablist]");
        this.panels = this.querySelector("[data-faq-panels]") || this;
        this.categories = Array.from(this.panels.querySelectorAll(".faq-category"));
        if (!this.tablist || this.categories.length < 2) return; // nothing to tab

        this.showAll = this.dataset.showAll !== "false";
        this.allLabel = this.dataset.allLabel || "All";

        this._buildTabs();
        this._activate(this.showAll ? "all" : "0");
        this.tablist.addEventListener("click", this._onClick);
        this.tablist.addEventListener("keydown", this._onKeydown);
      }

      disconnectedCallback() {
        this.tablist?.removeEventListener("click", this._onClick);
        this.tablist?.removeEventListener("keydown", this._onKeydown);
      }

      _makeTab(label, key) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "faqs__tab";
        btn.textContent = label;
        btn.dataset.index = key; // "all" or a category index
        btn.setAttribute("role", "tab");
        this.tablist.appendChild(btn);
        return btn;
      }

      _buildTabs() {
        this.tabs = [];
        if (this.showAll) this.tabs.push(this._makeTab(this.allLabel, "all"));
        this.categories.forEach((cat, i) => {
          const titleEl = cat.querySelector(".faq-category__title");
          const label = (titleEl && titleEl.textContent.trim()) || "Category " + (i + 1);
          this.tabs.push(this._makeTab(label, String(i)));
        });
        this.tablist.setAttribute("role", "tablist");
      }

      _onClick(e) {
        const btn = e.target.closest("[data-index]");
        if (btn) this._activate(btn.dataset.index);
      }

      // Arrow / Home / End keyboard navigation between tabs.
      _onKeydown(e) {
        const current = this.tabs.findIndex((t) => t.classList.contains("is-active"));
        let next = null;
        if (e.key === "ArrowRight" || e.key === "ArrowDown") next = (current + 1) % this.tabs.length;
        else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = (current - 1 + this.tabs.length) % this.tabs.length;
        else if (e.key === "Home") next = 0;
        else if (e.key === "End") next = this.tabs.length - 1;
        if (next === null) return;
        e.preventDefault();
        const key = this.tabs[next].dataset.index;
        this._activate(key);
        this.tabs[next].focus();
      }

      _activate(key) {
        const showAll = key === "all";
        this.categories.forEach((cat, i) => {
          cat.hidden = !(showAll || i === Number(key));
        });
        // State hook for styling the "All" view; no rules depend on it today.
        this.panels.classList.toggle("faqs__panels--all", showAll);
        this.tabs.forEach((t) => {
          const on = t.dataset.index === key;
          t.classList.toggle("is-active", on);
          t.setAttribute("aria-selected", on ? "true" : "false");
          t.tabIndex = on ? 0 : -1;
        });
      }
    },
  );
}
